/**
 * CreateChallengeForm — shown only to callers Central grants training:write in
 * the current workspace (the same capability the server requires). The server
 * validates everything again and decides the workspace.
 *
 * Goal kinds: completed attempts (mode / scenario tag / minimum score),
 * conversation minutes, NPC level, practice days, Learn lessons, attempts in a
 * practice language. Song learning is listed but disabled: it needs licensed
 * song content and the server refuses it.
 *
 * Modes: per person, cooperative (one team total), competitive (ranked among
 * participants who opt in).
 */

import { useState } from 'react';
import { useCreateChallengeMutation } from '../../store/api/challengesApi';
import { useGetLearningModulesQuery } from '../../store/api/learningApi';
import { PRACTICE_LANGUAGES } from '../../config/practiceLanguages';
import { TEAM_AGGREGATABLE_KINDS, type ChallengeCondition, type GoalScope } from './challengeTypes';
import { challengeWindowInstants, defaultChallengeWindow } from './challengeWindow';

type Kind = ChallengeCondition['kind'];
type AttemptMode = 'any' | 'training' | 'testing';

function errorMessage(e: unknown): string {
  const data = (e as { data?: { error?: string; message?: string; details?: Array<{ message: string }> | { status?: string } } })?.data;
  if (Array.isArray(data?.details) && data.details.length) return data.details.map((d) => d.message).join('; ');
  return data?.message ?? 'Could not create the challenge. Please try again.';
}

const KIND_OPTIONS: Array<{ value: Kind; label: string }> = [
  { value: 'conversation_attempts', label: 'Completed attempts' },
  { value: 'conversation_minutes', label: 'Conversation minutes' },
  { value: 'npc_level', label: 'Reach an NPC level' },
  { value: 'practice_days', label: 'Practice days' },
  { value: 'learning_lessons', label: 'Learn lessons completed' },
  { value: 'language_attempts', label: 'Attempts in a language' },
];

export function CreateChallengeForm({ onCreated }: { onCreated?: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<Kind>('conversation_attempts');
  const [goalScope, setGoalScope] = useState<GoalScope>('individual');
  const [target, setTarget] = useState(5);
  const [mode, setMode] = useState<AttemptMode>('testing');
  const [scenarioTag, setScenarioTag] = useState('');
  const [minOverall, setMinOverall] = useState('');
  const [personaKey, setPersonaKey] = useState('');
  const [language, setLanguage] = useState('es-ES');
  const [moduleSlug, setModuleSlug] = useState('');
  const [startsAt, setStartsAt] = useState(() => defaultChallengeWindow().startsAt);
  const [endsAt, setEndsAt] = useState(() => defaultChallengeWindow().endsAt);
  const [error, setError] = useState<string | null>(null);
  const [create, { isLoading }] = useCreateChallengeMutation();
  const { data: learn } = useGetLearningModulesQuery(undefined, { skip: kind !== 'learning_lessons' });

  const teamAllowed = TEAM_AGGREGATABLE_KINDS.includes(kind);
  const scope: GoalScope = goalScope === 'team' && !teamAllowed ? 'individual' : goalScope;
  const score = minOverall.trim() ? { minOverall: Number(minOverall) } : {};

  const condition = (): ChallengeCondition => {
    switch (kind) {
      case 'conversation_minutes':
      case 'practice_days':
        return { kind, target };
      case 'npc_level':
        return { kind, target, personaKey: personaKey.trim() };
      case 'learning_lessons':
        return { kind, target, ...(moduleSlug ? { moduleSlug } : {}) };
      case 'language_attempts':
        return { kind, target, language, mode, ...score };
      default:
        return { kind, target, mode, ...(scenarioTag.trim() ? { scenarioTag: scenarioTag.trim() } : {}), ...score };
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const created = await create({
        title: title.trim(),
        description: description.trim() || null,
        goalScope: scope,
        condition: condition(),
        ...challengeWindowInstants(startsAt, endsAt),
      }).unwrap();
      setTitle('');
      setDescription('');
      onCreated?.(created.id);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const attemptFields = kind === 'conversation_attempts' || kind === 'language_attempts';

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="create-challenge-form">
      <input className="input w-full" placeholder="Title" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} required />
      <textarea className="input w-full" rows={2} placeholder="Description (optional)" maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="flex flex-wrap gap-3">
        <label className="text-sm">
          Goal
          <select className="input ml-2" value={kind} onChange={(e) => setKind(e.target.value as Kind)} data-testid="challenge-kind">
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {/* Rights-gated: needs licensed song content (the server refuses it). */}
            <option value="song_learning" disabled>
              Song learning (not available yet)
            </option>
          </select>
        </label>
        <label className="text-sm">
          Target
          <input className="input ml-2 w-24" type="number" min={1} value={target} onChange={(e) => setTarget(Number(e.target.value))} />
        </label>
        <label className="text-sm">
          Mode
          <select className="input ml-2" value={scope} onChange={(e) => setGoalScope(e.target.value as GoalScope)} data-testid="challenge-mode">
            <option value="individual">Per person</option>
            <option value="team" disabled={!teamAllowed}>
              Cooperative (one team total)
            </option>
            <option value="competitive">Competitive (ranked, opt-in)</option>
          </select>
        </label>
      </div>
      {scope === 'competitive' && (
        <p className="text-xs text-gray-500">
          Only participants who choose to appear on the board (and whose profile is public with scores shown) are ranked.
          Anyone whose results are not yet measured is listed separately, never ranked as zero.
        </p>
      )}
      {attemptFields && (
        <div className="flex flex-wrap gap-3">
          {kind === 'language_attempts' && (
            <label className="text-sm">
              Language
              <select className="input ml-2" value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="challenge-language">
                {PRACTICE_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm">
            Session mode
            <select className="input ml-2" value={mode} onChange={(e) => setMode(e.target.value as AttemptMode)}>
              <option value="testing">Testing</option>
              <option value="training">Development</option>
              <option value="any">Either</option>
            </select>
          </label>
          {kind === 'conversation_attempts' && (
            <input className="input" placeholder="Scenario tag (optional)" maxLength={50} value={scenarioTag} onChange={(e) => setScenarioTag(e.target.value)} />
          )}
          <input className="input w-40" type="number" min={0} max={100} placeholder="Min. score (optional)" value={minOverall} onChange={(e) => setMinOverall(e.target.value)} />
        </div>
      )}
      {kind === 'npc_level' && (
        <input className="input" placeholder="NPC persona key" maxLength={150} value={personaKey} onChange={(e) => setPersonaKey(e.target.value)} required />
      )}
      {kind === 'learning_lessons' && (
        <label className="text-sm">
          Module
          <select className="input ml-2" value={moduleSlug} onChange={(e) => setModuleSlug(e.target.value)} data-testid="challenge-module">
            <option value="">Any Learn module</option>
            {(learn?.modules ?? []).map((m) => (
              <option key={m.slug} value={m.slug}>
                {m.title}
              </option>
            ))}
          </select>
        </label>
      )}
      {kind === 'practice_days' && (
        <p className="text-xs text-gray-500">A day counts once, however many sessions it had — the same days your streak counts.</p>
      )}
      <div className="flex flex-wrap gap-3">
        <label className="text-sm">
          From
          <input className="input ml-2" type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </label>
        <label className="text-sm">
          To
          <input className="input ml-2" type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </label>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" className="btn btn-primary" disabled={isLoading || !title.trim()}>
        Create challenge
      </button>
    </form>
  );
}

export default CreateChallengeForm;
